import useAuth from 'hooks/useAuth';
import { ReactNode, createContext, useCallback, useEffect, useState } from 'react';
import { HubConnection, HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { getApiUrl } from 'utils/urls/getUrls';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { isValidToken, setSession } from 'utils/jwt';
import { refreshAccessToken } from 'utils/oidcTokenService';

type SubscribeToCampaignMemberProcessingParams = {
  campaignId: number;
  onImportStarted: (count: number) => void;
  onCampaignMemberAdded: () => void;
  onCampaignMemberNotSent: (contactInfo: string, additionStatus: string) => void;
  onCampaignMemberAddFailed: (email: string, message: string) => void;
};

type SignalRContextProps = {
  subscribeToCampaignMemberProcessing: (
    params: SubscribeToCampaignMemberProcessingParams
  ) => () => void;
};

const initialState: SignalRContextProps = {
  subscribeToCampaignMemberProcessing: () => () => {}
};

const SignalRContext = createContext(initialState);

const getCurrentAccessToken = async () => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken && isValidToken(accessToken)) {
    return accessToken;
  }

  const tokenResponse = await refreshAccessToken();
  if (!tokenResponse) {
    return accessToken ?? '';
  }

  setSession(tokenResponse.access_token);
  return tokenResponse.access_token;
};

type SignalRProviderProps = {
  children: ReactNode;
};

function SignalRProvider({ children }: SignalRProviderProps) {
  const { selectedOrg, user } = useAuth();
  const [connection, setConnection] = useState<HubConnection>();
  const appInsights = useAppInsightsContext();

  useEffect(() => {
    if (!selectedOrg?.organizationId || !user) {
      return;
    }
    function createHubConnection() {
      const hubConnection = new HubConnectionBuilder()
        .withUrl(`${getApiUrl()}/hubs/campaign-members`, {
          transport: HttpTransportType.WebSockets,
          accessTokenFactory: getCurrentAccessToken
        })
        .withAutomaticReconnect()
        .build();

      setConnection(hubConnection);
      hubConnection
        .start()
        .catch((error) => {
          appInsights.trackException({ exception: error, properties: [{ userId: user?.userId }] });
        });
      return () => {
        hubConnection.stop();
      };
    }
    const stopper = createHubConnection();
    return stopper;
  }, [selectedOrg?.organizationId, user, appInsights]);

  const orgId = selectedOrg?.organizationId;

  // Returns an unsubscribe fn; memoized on [connection, orgId] so handlers (re)register once the
  // connection is ready and re-renders don't stack duplicate handlers (callers clean up via connection.off).
  const subscribeToCampaignMemberProcessing = useCallback(
    ({
      campaignId,
      onImportStarted,
      onCampaignMemberAdded,
      onCampaignMemberNotSent,
      onCampaignMemberAddFailed
    }: SubscribeToCampaignMemberProcessingParams) => {
      if (!connection) {
        return () => {};
      }

      const importStartedTarget = `CampaignMemberImportStarted${orgId}${campaignId}`;
      const addSucceededTarget = `CampaignMemberAddSucceeded${orgId}${campaignId}`;
      const addedButNotSentTarget = `CampaignMemberAddedButNotSent${orgId}${campaignId}`;
      const addFailedTarget = `CampaignMemberAddFailed${orgId}${campaignId}`;

      const importStartedHandler = (
        count: number,
        receivedCampaignId: number,
        receivedOrgId: string
      ) => {
        if (receivedCampaignId === campaignId && receivedOrgId === orgId) {
          onImportStarted(count);
        }
      };
      const addSucceededHandler = (receivedCampaignId: number, receivedOrgId: string) => {
        if (receivedCampaignId === campaignId && receivedOrgId === orgId) {
          onCampaignMemberAdded();
        }
      };
      const addedButNotSentHandler = (
        receivedCampaignId: number,
        campaignMemberIdentifier: string,
        additionStatus: string,
        receivedOrgId: string
      ) => {
        if (receivedCampaignId === campaignId && receivedOrgId === orgId) {
          onCampaignMemberNotSent(campaignMemberIdentifier, additionStatus);
        }
      };
      const addFailedHandler = (
        receivedCampaignId: number,
        campaignMemberIdentifier: string,
        message: string,
        receivedOrgId: string
      ) => {
        if (receivedCampaignId === campaignId && receivedOrgId === orgId) {
          onCampaignMemberAddFailed(campaignMemberIdentifier, message);
        }
      };

      connection.on(importStartedTarget, importStartedHandler);
      connection.on(addSucceededTarget, addSucceededHandler);
      connection.on(addedButNotSentTarget, addedButNotSentHandler);
      connection.on(addFailedTarget, addFailedHandler);

      return () => {
        connection.off(importStartedTarget, importStartedHandler);
        connection.off(addSucceededTarget, addSucceededHandler);
        connection.off(addedButNotSentTarget, addedButNotSentHandler);
        connection.off(addFailedTarget, addFailedHandler);
      };
    },
    [connection, orgId]
  );

  return (
    <SignalRContext.Provider
      value={{
        subscribeToCampaignMemberProcessing
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
}

export { SignalRProvider, SignalRContext };
